import { z } from "zod";
import { MediaContactTagStatus } from "../lib/enums";

/**
 * Photo contact tags: a face-anchored square on an event gallery photo that
 * names a contact. Coordinates are normalized 0–1 floats relative to the
 * image, so one stored box maps onto any served variant.
 *
 * Shared between the member upload flow (input) and the admin review surfaces
 * (output) — see issues #207/#208.
 */

/** One tag as submitted with an upload: who is in the box, and where it sits. */
export const MediaContactTagInputSchema = z.object({
  contact_id: z.string().min(1),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});
export type MediaContactTagInput = z.infer<typeof MediaContactTagInputSchema>;

/**
 * Uploader-side contact search for the tag autocomplete. Deliberately not the
 * admin contacts list: gated by contribution eligibility rather than admin
 * role, and the projection carries no PII — an id and a display name, nothing
 * else.
 *
 * `event_id` is the non-admin authorization scope (contribution eligibility is
 * per-event) and may be omitted by admins, who pass the gate outright — the
 * lightbox tag editor (#212) searches from surfaces without event context.
 */
export const TagNameSearchInputSchema = z.object({
  event_id: z.string().optional(),
  q: z.string().min(1).max(200),
  limit: z.number().int().min(1).max(20).default(20),
});
export type TagNameSearchInput = z.infer<typeof TagNameSearchInputSchema>;

/** One autocomplete option: the only two contact fields uploaders may see. */
export const TagNameOptionSchema = z.object({
  id: z.string(),
  display_name: z.string(),
});
export type TagNameOption = z.infer<typeof TagNameOptionSchema>;

export const TagNameSearchOutputSchema = z.array(TagNameOptionSchema);
export type TagNameSearchOutput = z.infer<typeof TagNameSearchOutputSchema>;

/** One stored tag as review surfaces read it, with the contact's display name. */
export const MediaContactTagSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  contact_display_name: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  status: z.nativeEnum(MediaContactTagStatus),
});
export type MediaContactTag = z.infer<typeof MediaContactTagSchema>;
